/* =========================================================================
   COMPONENT: ATELIER PILLS (Dynamic Selection List with Thumbnails)
   ========================================================================= */
class AtelierPills extends HTMLElement {
  
  // 1. Tell the component to watch its own variant ID attribute
  static get observedAttributes() {
    return ['data-variant-id'];
  }

  connectedCallback() {
    if (this.hasAttribute('readonly')) {
      // SUBSCRIPTION MODE: Render immediately and set up variant detection
      this.renderSubscriptionPills();
      this.setupVariantDetection();
    } else {
      // BUILDER MODE: Listen to user interaction state
      document.addEventListener('AtelierBuilder:QtyChanged', () => this.renderBuilderPills());
      setTimeout(() => this.renderBuilderPills(), 100);
    }
  }

  // 2. Automatically re-render if the attribute changes
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-variant-id' && oldValue !== newValue && this.hasAttribute('readonly')) {
      this.renderSubscriptionPills();
    }
  }

  // 3. Smart Variant Detection (Listens to the page for changes)
  setupVariantDetection() {
    const updateFromURL = () => {
      const params = new URLSearchParams(window.location.search);
      const urlVariantId = params.get('variant');
      if (urlVariantId && urlVariantId !== this.dataset.variantId) {
        this.dataset.variantId = urlVariantId;
      }
    };

    document.addEventListener('change', (e) => {
      const form = e.target.closest('form[action*="/cart/add"], variant-selects, variant-radios');
      if (form) {
        const idInput = form.querySelector('[name="id"]');
        if (idInput && idInput.value && idInput.value !== this.dataset.variantId) {
          this.dataset.variantId = idInput.value;
        }
      } else if (e.target.name === 'id' && e.target.value !== this.dataset.variantId) {
        this.dataset.variantId = e.target.value;
      }
    });

    const originalPushState = history.pushState;
    history.pushState = function() {
      originalPushState.apply(this, arguments);
      updateFromURL();
    };
    
    const originalReplaceState = history.replaceState;
    history.replaceState = function() {
      originalReplaceState.apply(this, arguments);
      updateFromURL();
    };

    window.addEventListener('popstate', updateFromURL);
  }

  // --- MODE 1: SUBSCRIPTION (READONLY) ---
  renderSubscriptionPills() {
    const variantId = this.dataset.variantId;
    const dataNode = document.getElementById('SubscriptionPillsData');
    if (!dataNode || !variantId) return;

    let allData;
    try { 
      allData = JSON.parse(dataNode.textContent); 
    } catch(e) { 
      console.error("Atelier Pills: Invalid JSON data");
      return; 
    }

    const items = allData[variantId];
    if (!items || !items.length) {
      this.innerHTML = '<span class="atelier-pill-empty">Curating your selection...</span>';
      return;
    }

    this.innerHTML = '';
    
    items.forEach(item => {
      const pill = document.createElement('div');
      pill.className = 'atelier-pill js-modal-source'; 
      pill.style.cursor = 'pointer'; 
      
      // Safely parse the quantity from the JSON
      const qty = parseInt(item.qty, 10) || 1;
      const countOverlay = qty > 1 ? `<span class="atelier-pill__count">${qty}</span>` : '';
      
      pill.innerHTML = `
        <div class="atelier-pill__thumb" style="background-image: url('${item.thumb}')">
          ${countOverlay}
        </div>
        <span class="atelier-pill__name">${item.title}</span>
        
        <script class="js-card-data" type="application/json">${JSON.stringify(item)}</script>
      `;
      
      pill.addEventListener('click', () => {
        if (window.AtelierModalUI) window.AtelierModalUI.openModal(pill);
      });
      
      this.appendChild(pill);
    });
  }

  // --- MODE 2: BUILD-A-BOX (INTERACTIVE) ---
  renderBuilderPills() {
    const state = window.AtelierBuilderState;
    if (!state || !state.selection) return;

    this.innerHTML = '';
    let hasItems = false;

    Object.keys(state.selection).forEach((id) => {
      const qty = state.selection[id];
      
      if (qty > 0) {
        hasItems = true;
        
        const card = document.querySelector(`.atelier-card[data-variant-id="${id}"]`);
        const name = card ? card.querySelector('.atelier-card__name').textContent.trim() : 'Chocolate';
        const imgEl = card ? card.querySelector('img') : null;
        const imgSrc = imgEl ? (imgEl.currentSrc || imgEl.src) : '';

        const countOverlay = qty > 1 ? `<span class="atelier-pill__count">${qty}</span>` : '';

        const pill = document.createElement('div');
        pill.className = 'atelier-pill';
        pill.style.cursor = 'pointer'; 
        pill.innerHTML = `
          <div class="atelier-pill__thumb" style="background-image: url('${imgSrc}')">
            ${countOverlay}
          </div>
          <span class="atelier-pill__name">${name}</span>
          <button type="button" class="atelier-pill__remove" aria-label="Remove ${name}">
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        `;
        
        // Modal Trigger
        pill.addEventListener('click', (e) => {
          if (e.target.closest('.atelier-pill__remove')) return;
          const freshCard = document.querySelector(`.atelier-card[data-variant-id="${id}"]`);
          const cardWrap = freshCard ? freshCard.closest('.atelier-card-wrap, .js-modal-source') : null;
          if (cardWrap && window.AtelierModalUI) window.AtelierModalUI.openModal(cardWrap);
        });

        // Remove Button Logic
        const removeBtn = pill.querySelector('.atelier-pill__remove');
        removeBtn.addEventListener('click', async (e) => {
          e.stopPropagation(); 
          const freshCard = document.querySelector(`.atelier-card[data-variant-id="${id}"]`);
          const freshMinusBtn = freshCard ? freshCard.querySelector('.js-card-minus') : null;
          
          if (!freshMinusBtn) return;

          if (qty > 1) {
            const confirmed = await window.AtelierDialog.open({
              title: 'Remove Chocolates',
              text: `You have ${qty} of these in your box. Do you want to remove all of them?`,
              confirmText: 'Remove all',
              cancelText: 'Cancel'
            });
            
            if (confirmed) {
              let clicksRemaining = qty;
              const clickInterval = setInterval(() => {
                const targetBtn = document.querySelector(`.atelier-card[data-variant-id="${id}"] .js-card-minus`);
                if (clicksRemaining > 0 && targetBtn) {
                  targetBtn.click();
                  clicksRemaining--;
                } else {
                  clearInterval(clickInterval);
                }
              }, 25);
            }
          } else {
            freshMinusBtn.click();
          }
        });

        this.appendChild(pill);
      }
    });

    if (!hasItems) {
      this.innerHTML = '<span class="atelier-pill-empty">Your box is empty</span>';
    }
  }
}

if (!customElements.get('atelier-pills')) {
  customElements.define('atelier-pills', AtelierPills);
}